// react

// types
import { Document, Program as ProgramType, Project } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Box, Grid, Skeleton, SxProps, Typography } from '@mui/material';

// ui
import { Flag } from '../Flag/Flag';

// props
export interface ProgramProps {
	isLoading?: boolean;
	project?: Project;
}

export const Program = ({ isLoading, project }: ProgramProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const program = project?.program as ProgramType;
	const description = program?.description;
	const hero = program?.hero;
	const logo = program?.logo;
	const name = program?.name;

	let urlHero, urlLogo;

	if ((hero as Document)?.blob && project) {
		const heroDoc = hero as Document;
		urlHero = `data:${heroDoc.metadata.type};base64,${heroDoc.blob}`;
	}

	if ((logo as Document)?.blob && project) {
		const logoDoc = logo as Document;
		urlLogo = `data:${logoDoc.metadata.type};base64,${logoDoc.blob}`;
	}

	if (isLoading) {
		return <Skeleton height={240} variant="rectangular" />;
	} else if (name) {
		return (
			<Grid
				alignItems="stretch"
				container
				direction="column"
				spacing={2}
				sx={{ height: 'calc(100% + 16px)' }}>
				<Grid flex={1} item>
					<Box
						sx={(theme) => {
							return {
								backgroundColor: theme.palette.brand.blue[700],
								position: 'relative',
								height: '100%',
								color: theme.palette.common.white,
								overflow: 'hidden',
							};
						}}>
						{urlHero && (
							<>
								<Box
									alt={t('preview')}
									component="img"
									src={urlHero}
									sx={(theme) => {
										return {
											position: 'absolute',
											top: 0,
											left: 0,
											objectFit: 'cover',
											width: '100%',
											height: '100%',
										};
									}}
								/>
								<Box
									sx={(theme) => {
										return {
											background: `linear-gradient(to top, #000, transparent)`,
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											height: '100%',
											minHeight: '200px',
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
										};
									}}
								/>
							</>
						)}
						<Grid
							justifyContent="space-between"
							container
							direction="column"
							spacing={2}
							sx={{
								minHeight: 300,
								height: 'calc(100% + 16px)',
								position: 'relative',
							}}>
							{logo && (
								<Grid alignSelf="flex-end" item>
									<Box
										sx={(theme) => {
											return {
												backgroundColor: theme.palette.common.white,
												borderRadius: 1,
												padding: theme.spacing(1),
												margin: theme.spacing(3),
												width: {
													xs: 60,
													lg: 90,
												},
												height: {
													xs: 60,
													lg: 90,
												},
											};
										}}>
										<Box
											sx={(theme) => {
												let sx: SxProps = {
													borderColor: theme.palette.divider,
													borderStyle: 'solid',
													borderWidth: 1,
													borderRadius: 1,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													width: '100%',
													height: '100%',
													color: theme.palette.brand.grey[500],
													overflow: 'hidden',
												};

												if (!logo) {
													sx = {
														...sx,
														display: 'none',
													};
												}

												return sx;
											}}>
											{urlLogo ? (
												<Box
													alt={t('preview')}
													component="img"
													src={urlLogo}
													sx={(theme) => {
														return {
															objectFit: 'contain',
															width: '100%',
															height: '100%',
														};
													}}
												/>
											) : (
												<Typography fontWeight={600} variant="caption">
													{t('logo')}
												</Typography>
											)}
										</Box>
									</Box>
								</Grid>
							)}
							<Grid id="text" item>
								<Box
									sx={(theme) => {
										return {
											padding: theme.spacing(3),
										};
									}}>
									<Grid container spacing={2}>
										{project && (
											<Grid item xs={12}>
												<Flag code={project.country.code} />
											</Grid>
										)}
										<Grid item xs={12}>
											<Typography variant="h1">{name}</Typography>
											<Typography sx={{ wordBreak: 'break-all' }}>{description}</Typography>
										</Grid>
									</Grid>
								</Box>
							</Grid>
						</Grid>
					</Box>
				</Grid>
			</Grid>
		);
	}

	return <></>;
};
